import { useState } from 'react';
import { useApp, useCan } from '../../app/AppContext.js';
import { formatBRL, formatDate } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  Dash,
  EmptyState,
  ErrorBlock,
  KV,
  KVRow,
  Loading,
  PageHeader,
} from '../components/primitives.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field, TextInput } from '../components/Field.js';
import { useToast } from '../components/Toast.js';
import { IconCheck, IconCopy, IconPlay, IconSearch, IconUsers } from '../components/icons.js';
import type { Charge, Customer, CustomerInvoice } from '../../data/port.js';

type Resultado =
  | { estado: 'inicial' }
  | { estado: 'carregando' }
  | { estado: 'erro'; mensagem: string }
  | { estado: 'ok'; clientes: Customer[] };

export function Clientes() {
  const { data } = useApp();
  const toast = useToast();
  const podeEmitir = useCan('console-operador');

  const [contrato, setContrato] = useState('');
  const [cpf, setCpf] = useState('');
  const [nome, setNome] = useState('');
  const [resultado, setResultado] = useState<Resultado>({ estado: 'inicial' });
  const [emitindo, setEmitindo] = useState<string | null>(null);
  const [emitida, setEmitida] = useState<Charge | null>(null);

  const limpar = () => {
    setContrato('');
    setCpf('');
    setNome('');
    setResultado({ estado: 'inicial' });
  };

  const buscar = async () => {
    if (!contrato.trim() && !cpf.trim() && !nome.trim()) {
      setResultado({ estado: 'erro', mensagem: 'Informe ao menos um filtro: contrato, CPF/CNPJ ou nome.' });
      return;
    }
    setResultado({ estado: 'carregando' });
    try {
      const clientes = await data.findCustomers({
        ...(contrato.trim() ? { contract: contrato.trim() } : {}),
        ...(cpf.trim() ? { taxId: cpf.trim() } : {}),
        ...(nome.trim() ? { name: nome.trim() } : {}),
      });
      setResultado({ estado: 'ok', clientes });
    } catch (e) {
      setResultado({ estado: 'erro', mensagem: e instanceof Error ? e.message : 'Falha ao buscar cliente.' });
    }
  };

  const emitir = async (cliente: Customer, titulo: CustomerInvoice) => {
    setEmitindo(titulo.invoiceId);
    try {
      const cobranca = await data.createCharge({
        invoiceId: titulo.invoiceId,
        ...(cliente.name ? { payerName: cliente.name } : {}),
        ...(cliente.taxId ? { payerDocument: cliente.taxId } : {}),
      });
      setEmitida(cobranca);
      toast.success(`Cobrança emitida — título ${cobranca.invoiceId}`, formatBRL(cobranca.amountCents));
    } catch (e) {
      toast.error('Falha ao emitir cobrança', e instanceof Error ? e.message : undefined);
    } finally {
      setEmitindo(null);
    }
  };

  const colunas = (cliente: Customer): Column<CustomerInvoice>[] => [
    { key: 'titulo', header: 'Título', cell: (t) => <span className="num td--strong">{t.invoiceId}</span> },
    { key: 'vence', header: 'Vence', cell: (t) => formatDate(t.dueDate) ?? <Dash /> },
    { key: 'valor', header: 'Valor', num: true, cell: (t) => formatBRL(t.amountCents) },
    {
      key: 'atraso',
      header: 'Atraso',
      cell: (t) =>
        t.daysOverdue && t.daysOverdue > 0 ? (
          <Badge tone="warn">{t.daysOverdue}d</Badge>
        ) : (
          <span className="faint">em dia</span>
        ),
    },
    ...(podeEmitir
      ? [
          {
            key: 'acao',
            header: '',
            align: 'right' as const,
            cell: (t: CustomerInvoice) => (
              <Button
                variant="primary"
                size="sm"
                loading={emitindo === t.invoiceId}
                onClick={() => void emitir(cliente, t)}
              >
                <IconPlay size={14} /> Emitir
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader title="Clientes" desc="Busca no SGP por contrato, CPF/CNPJ ou nome — e emite a cobrança do título." />

      <Card>
        <div className="card__body">
          <div className="form-row">
            <Field label="Contrato">
              <TextInput value={contrato} onChange={setContrato} mono placeholder="1302" onEnter={() => void buscar()} />
            </Field>
            <Field label="CPF/CNPJ">
              <TextInput value={cpf} onChange={setCpf} mono placeholder="248.386.290-01" onEnter={() => void buscar()} />
            </Field>
            <Field label="Nome">
              <TextInput value={nome} onChange={setNome} placeholder="maria" onEnter={() => void buscar()} />
            </Field>
          </div>
          <div className="row" style={{ marginTop: '1rem', gap: 'var(--space-3)' }}>
            <Button variant="primary" onClick={() => void buscar()} loading={resultado.estado === 'carregando'}>
              <IconSearch size={16} /> Buscar
            </Button>
            <Button variant="ghost" onClick={limpar}>Limpar</Button>
            <span className="faint right" style={{ fontSize: 'var(--text-sm)' }}>
              Busque por contrato, CPF/CNPJ ou nome
            </span>
          </div>
        </div>
      </Card>

      {resultado.estado === 'carregando' && <Card><Loading label="Consultando o SGP…" /></Card>}
      {resultado.estado === 'erro' && <ErrorBlock message={resultado.mensagem} />}
      {resultado.estado === 'ok' && resultado.clientes.length === 0 && (
        <Card>
          <EmptyState
            icon={<IconUsers size={40} />}
            title="Nenhum cliente com esse filtro"
            desc="Confira o contrato, CPF/CNPJ ou nome digitado."
          />
        </Card>
      )}
      {resultado.estado === 'ok' && resultado.clientes.length > 0 && (
        <>
          <div className="result-count">
            {resultado.clientes.length} cliente(s) ·{' '}
            {resultado.clientes.reduce((soma, c) => soma + c.invoices.length, 0)} título(s) em aberto
          </div>
          {resultado.clientes.map((cliente, i) => {
            const abertos = cliente.invoices.length;
            return (
              <Card key={`${cliente.taxId ?? cliente.name}-${i}`}>
                <div className={`client-head${abertos > 0 ? ' client-head--bordered' : ''}`}>
                  <div className="client-head__id">
                    <span className="client-head__name">{cliente.name}</span>
                    <span className="client-head__meta">
                      {cliente.taxId && <span className="num">{cliente.taxId}</span>}
                      {cliente.contracts.length > 0 && (
                        <span>contrato {cliente.contracts.join(', ')}</span>
                      )}
                    </span>
                  </div>
                  <div className="client-head__right">
                    {abertos > 0 ? (
                      <Badge tone="warn" dot>
                        {abertos} em aberto
                      </Badge>
                    ) : (
                      <span className="client-empty">
                        <IconCheck size={14} /> sem títulos em aberto
                      </span>
                    )}
                  </div>
                </div>
                {abertos > 0 && (
                  <div className="card__body--flush">
                    <DataTable columns={colunas(cliente)} rows={cliente.invoices} keyOf={(t) => t.invoiceId} />
                  </div>
                )}
              </Card>
            );
          })}
        </>
      )}

      {emitida && <ResultadoEmissao cobranca={emitida} onClose={() => setEmitida(null)} />}
    </>
  );
}

function ResultadoEmissao({ cobranca, onClose }: { cobranca: Charge; onClose: () => void }) {
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);

  const copiar = async () => {
    if (!cobranca.link) return;
    try {
      await navigator.clipboard?.writeText(cobranca.link);
      setCopiado(true);
      toast.success('Copiado', 'PIX copia-e-cola na área de transferência.');
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      /* clipboard bloqueado */
    }
  };

  return (
    <Modal
      title={`Cobrança emitida — título ${cobranca.invoiceId}`}
      sub="PIX gerado no gateway ativo. Envie o copia-e-cola ao cliente."
      onClose={onClose}
      footer={<Button variant="primary" onClick={onClose}>Fechar</Button>}
    >
      <KV>
        <KVRow k="Valor"><span className="num">{formatBRL(cobranca.amountCents)}</span></KVRow>
        <KVRow k="Status"><Badge tone="info" dot>{cobranca.status}</Badge></KVRow>
        {cobranca.end2end && <KVRow k="Transação"><span className="num">{cobranca.end2end}</span></KVRow>}
      </KV>

      {cobranca.link ? (
        <div className="field" style={{ marginTop: '1rem' }}>
          <label className="field__label">PIX copia-e-cola</label>
          <div className="input-group">
            <input className="input input--mono" readOnly value={cobranca.link} />
            <button type="button" className="btn btn--subtle btn--icon" onClick={() => void copiar()} title={copiado ? 'Copiado' : 'Copiar'}>
              <IconCopy size={15} />
            </button>
          </div>
        </div>
      ) : (
        <p className="muted" style={{ marginTop: '1rem' }}>O gateway não devolveu um código PIX.</p>
      )}
    </Modal>
  );
}
